// AuthContext — global user state, login/logout, session persistence.
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API } from '../utils/api';

// Always send cookies on API calls
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);        // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (err) {
      console.warn('Logout request failed; clearing session locally', err);
    }
    setUser(false);
    window.location.href = '/login';
  }, []);

  const value = useMemo(() => ({
    user, loading, login, logout, checkAuth, refresh: checkAuth,
    hasPermission: (perm) => Array.isArray(user?.permissions) && user.permissions.includes(perm),
    isRole: (...roles) => roles.includes(user?.role),
  }), [user, loading, login, logout, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
